import React from "react";
import styled, { css } from "styled-components";
import { CheckboxInput } from "../Checkbox";
import { ResourceIcon } from "../ResourceItem";
import { ProductIconType } from "../constants/productIcon";
import { style } from "../style";
import { LabelText as BaseLabelText } from "../utils/CheckAndRadioUtils";
import { focusOutline } from "../utils/focusOutlineStyle";
import { Heading } from "../Heading";

const TABLE_BORDER_STYLE = `${style.vars.borderWidth.base} solid ${style.colors.grey.light}`;
const TRANSITION_STYLE = `all ${style.vars.transitionSpeed.base} ${style.vars.easeInOutCubic}`;

const StyledResourceIcon = styled(ResourceIcon)`
  margin-right: ${style.vars.space["2"]};
`;

const Row = styled.label<{ disabled?: boolean }>`
  display: flex;
  flex-direction: row;
  flex-shrink: 1;
  justify-content: space-between;
  align-items: center;
  padding: 0 ${style.vars.space["3"]};
  border-left: ${TABLE_BORDER_STYLE};
  border-right: ${TABLE_BORDER_STYLE};
  border-top: ${TABLE_BORDER_STYLE};
  min-height: ${style.vars.space["6"]};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  text-transform: unset;

  ${({ disabled }) =>
    disabled &&
    css`
      & > * {
        opacity: ${style.vars.disabledOpacity};
      }
    `};

  &:active,
  &:focus,
  &:focus-within {
    outline: 0;
    &:not([disabled]) {
      ${focusOutline({
        top: "-" + style.vars.whitespace.xsmall,
        bottom: "-" + style.vars.whitespace.xsmall,
        left: "-" + style.vars.whitespace.xsmall,
        right: "-" + style.vars.whitespace.xsmall,
      })}
    }
  }

  ${CheckboxInput} {
    margin-right: ${style.vars.space["3"]};
    flex-shrink: 0;
  }
`;

const HeaderRow = styled(Row)<{ $isSelectAll: boolean }>`
  border-top: ${TABLE_BORDER_STYLE};
  border-top-left-radius: ${style.vars.borderRadius.base};
  border-top-right-radius: ${style.vars.borderRadius.base};
  background-color: ${({ $isSelectAll }): string => ($isSelectAll ? style.colors.white : style.colors.grey.lightest)};
  transition: ${TRANSITION_STYLE};
`;

const Body = styled.div<{ $isSelectAll: boolean }>`
  overflow-y: auto;
  max-height: ${({ $isSelectAll }): number => ($isSelectAll ? 0 : 200)}px;
  border-bottom: ${TABLE_BORDER_STYLE};
  border-bottom-left-radius: ${style.vars.borderRadius.base};
  border-bottom-right-radius: ${style.vars.borderRadius.base};
  transition: ${TRANSITION_STYLE};
  margin-bottom: ${style.vars.space["3"]};
`;

const BodyRow = styled(Row)``;

const LabelText = styled(BaseLabelText)`
  font-weight: ${style.vars.fontWeight.bold};
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Description = styled.div`
  color: ${style.colors.grey.dark};
  flex-shrink: 0;
`;

type MultiSelectListItem = {
  name: string;
  [key: string]: any;
};

/**
 * This represents any object passed into the multi-select table
 * with the isChecked state added to it.
 */
export type FormikOption = {
  name: string;
  description?: string;
  /** `status` will be passed to `ResourceIcon` if provided alongside `productIcon` */
  status?: string;
  disabled?: boolean;
  isChecked: boolean;
  [key: string]: any;
};

/**
 * adds the `isChecked` state to a list of items
 */
export const convertToFormikOptionsList = (resourceList: MultiSelectListItem[] | null | undefined): FormikOption[] =>
  (resourceList ?? []).map((opt) => ({ ...opt, isChecked: false }));

/**
 * returns true if all items are checked
 */
export const multiSelectListallSelected = (resourceList: FormikOption[]): boolean => resourceList.every(({ isChecked }) => isChecked);

/**
 * returns the total count of checked items
 */
const countCheckedItems = (resourceList: FormikOption[]): number =>
  resourceList.reduce((acc, { isChecked }) => (isChecked ? (acc += 1) : acc), 0);

/**
 * returns true if any of the items are disabled
 */
const shouldDisableSelectAll = (resourceList: FormikOption[]): boolean => resourceList.some(({ disabled }) => disabled);

type Props = {
  id: string;
  /** specifies the field to be used as a unique identifier */
  idProp?: string;
  selectAllLabel?: string;
  formikFieldName: string;
  title?: string;
  items: FormikOption[];
  productIcon?: ProductIconType | ((item: FormikOption) => ProductIconType | undefined);
  onChange?: (item: FormikOption) => void;
  onAllChange?: (isAllSelected: boolean) => void;
  setFieldValue?: (formikFieldName: string, value: FormikOption[] | boolean, shouldValidate: boolean | undefined) => void;
} & (
  | {
      onChange: (item: FormikOption) => void;
      onAllChange: (isAllSelected: boolean) => void;
      setFieldValue?: never;
    }
  | {
      onChange?: never;
      onAllChange?: never;
      setFieldValue: (formikFieldName: string, value: FormikOption[] | boolean, shouldValidate: boolean | undefined) => void;
    }
);

export const MultiSelectList: React.FC<Props> = ({
  id,
  idProp = "id",
  selectAllLabel = "Select All",
  formikFieldName,
  title,
  items,
  setFieldValue,
  productIcon,
  onChange,
  onAllChange,
}) => {
  const numberSelected = countCheckedItems(items);
  const isSelectAll = numberSelected === items.length;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const selectAllDisabled = shouldDisableSelectAll(items);
  const selectAllId = `${id}-select-all`;
  const selectAllLabelEltId = `${id}-select-all-label`;

  return (
    <>
      {title && <Heading level="h5">{title}</Heading>}
      <HeaderRow $isSelectAll={isSelectAll} disabled={selectAllDisabled}>
        <CheckboxInput
          id={selectAllId}
          type="checkbox"
          aria-labelledby={selectAllLabelEltId}
          checked={isSelectAll}
          value={isSelectAll}
          disabled={selectAllDisabled}
          onChange={(): void => {
            if (onAllChange) {
              onAllChange(!isSelectAll);
              return;
            } else {
              setFieldValue(
                formikFieldName,
                items.map<FormikOption>((opt) => ({
                  ...opt,
                  isChecked: !isSelectAll,
                })),
                false
              );
            }
          }}
        />
        <LabelText id={selectAllLabelEltId} checked={isSelectAll} as="div">
          {selectAllLabel}
        </LabelText>
        <strong>
          {numberSelected} / {items.length}
        </strong>
      </HeaderRow>
      <Body $isSelectAll={isSelectAll}>
        {items.map((option: FormikOption, index) => {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          const { name, description, status, disabled, isChecked } = option;
          const uniqueId = option[idProp];
          const labelEltId = `${uniqueId}-label`;
          const descriptionEltId = `${uniqueId}-description`;
          const _productIcon = typeof productIcon === "function" ? productIcon(option) : productIcon;
          return (
            <BodyRow key={uniqueId} disabled={disabled}>
              <CheckboxInput
                id={uniqueId}
                type="checkbox"
                aria-labelledby={labelEltId}
                aria-describedby={description ? descriptionEltId : undefined}
                checked={isChecked}
                value={isChecked}
                disabled={disabled}
                onChange={(): void => {
                  if (onChange) {
                    onChange(option);
                  } else {
                    setFieldValue(`${formikFieldName}[${index}].isChecked`, !isChecked, false);
                  }
                }}
              />
              {_productIcon && <StyledResourceIcon icon={_productIcon} status={status} inline />}
              <LabelText id={labelEltId} checked={isChecked} as="div">
                {name}
              </LabelText>
              {!!description && <Description id={descriptionEltId}>{description}</Description>}
            </BodyRow>
          );
        })}
      </Body>
    </>
  );
};
